import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { finalize, merge, Subject, takeUntil } from 'rxjs';

import { AntonniaContractHistoryAction } from '../../../../modules/organizations/dtos/antonnia-contract-history-action';
import { AntonniaContractHistoryDetail } from '../../../../modules/organizations/dtos/antonnia-contract-history-detail';
import { AntonniaContractHistoryItem } from '../../../../modules/organizations/dtos/antonnia-contract-history-item';
import { OrganizationsService } from '../../../../modules/organizations/organizations.service';

import { ContractHistoryDialogData } from './contract-history-dialog-data';

@Component({
	selector: 'app-contract-history-dialog',
	styleUrls: ['./contract-history-dialog.component.scss'],
	templateUrl: './contract-history-dialog.component.html',
})
export class ContractHistoryDialogComponent implements OnInit, OnDestroy {
	detail: AntonniaContractHistoryDetail | null = null;
	detailError: boolean = false;
	historyError: boolean = false;
	isLoadingDetail: boolean = false;
	isLoadingHistory: boolean = false;
	items: AntonniaContractHistoryItem[] = [];
	selectedVersionId: string | null = null;
	total: number = 0;

	private readonly cancelDetail$ = new Subject<void>();
	private readonly pageSize: number = 20;
	private readonly unsubscribe$ = new Subject<void>();

	constructor(
		@Inject(MAT_DIALOG_DATA) readonly data: ContractHistoryDialogData,
		private readonly organizationsService: OrganizationsService,
	) {}

	get canLoadMore(): boolean {
		return this.items.length < this.total;
	}

	actionLabel(action: AntonniaContractHistoryAction): string {
		const labels: Record<AntonniaContractHistoryAction, string> = {
			BASELINE: 'Estado inicial do histórico',
			CREATE: 'Contrato criado',
			UPDATE: 'Contrato atualizado',
		};

		return labels[action];
	}

	diffLabel(type: 'added' | 'removed' | 'unchanged'): string {
		return {
			added: 'Adicionada',
			removed: 'Removida',
			unchanged: 'Sem alteração',
		}[type];
	}

	loadHistory(reset: boolean = false): void {
		if (this.isLoadingHistory) {
			return;
		}

		if (reset) {
			this.items = [];
			this.total = 0;
		}

		this.historyError = false;
		this.isLoadingHistory = true;
		this.organizationsService
			.getAntonniaContractHistory(
				this.data.organizationId,
				this.data.contract.id,
				this.items.length,
				this.pageSize,
			)
			.pipe(
				takeUntil(this.unsubscribe$),
				finalize(() => (this.isLoadingHistory = false)),
			)
			.subscribe({
				error: () => (this.historyError = true),
				next: (page) => {
					this.items = [...this.items, ...page.items];
					this.total = page.total;

					if (!this.selectedVersionId && this.items[0]) {
						this.selectVersion(this.items[0]);
					}
				},
			});
	}

	ngOnDestroy(): void {
		this.cancelDetail$.next();
		this.cancelDetail$.complete();
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	ngOnInit(): void {
		this.loadHistory();
	}

	retryDetail(): void {
		const selected = this.items.find(
			(item) => item.id === this.selectedVersionId,
		);

		if (selected) {
			this.selectVersion(selected);
		}
	}

	selectVersion(version: AntonniaContractHistoryItem): void {
		this.cancelDetail$.next();
		this.selectedVersionId = version.id;
		this.detail = null;
		this.detailError = false;
		this.isLoadingDetail = true;
		this.organizationsService
			.getAntonniaContractHistoryDetail(
				this.data.organizationId,
				this.data.contract.id,
				version.id,
			)
			.pipe(
				takeUntil(merge(this.unsubscribe$, this.cancelDetail$)),
				finalize(() => (this.isLoadingDetail = false)),
			)
			.subscribe({
				error: () => (this.detailError = true),
				next: (detail) => (this.detail = detail),
			});
	}

	trackByVersionId(
		_index: number,
		version: AntonniaContractHistoryItem,
	): string {
		return version.id;
	}
}
